import React from 'react';
import PropTypes from 'prop-types';

import ReactSelect, { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import AsyncSelect from 'react-select/async';

import Icon from '~/components/Icon/Icon';
import ExampleTooltip from '~/components/ExampleTooltip/ExampleTooltip';
import Translate from '~/components/Translate/Translate';
import Tooltip from '@sumologic/yama-ds-components/components/Tooltip';

//sst: custom option render
const Option = props => {
    const data = props?.data || {};

    const iconType = data.icon || null;

    const tooltip = data.tooltip;
    const example = data.example;

    const tip = tooltip || example;

    const content = (
        <span
            title={data.title}
            className={`${data.status ? 'with' : 'no'}-status`}
        >
            {iconType && <Icon type={iconType} />}
            {props.children}
            {data.status && (
                <Icon
                    type="circle2"
                    className={`${
                        'available' === data.status ? 'green' : 'red'
                    }-circle`}
                />
            )}
        </span>
    );

    return (
        <components.Option {...props}>
            {tip ? (
                <Tooltip
                    title={<ExampleTooltip example={tip} />}
                    placement="auto"
                    arrow
                >
                    <span>
                        {content}
                    </span>
                </Tooltip>
            ) : (
                content
            )}
        </components.Option>
    );
};

Option.propTypes = {
    data: PropTypes.shape({
        title: PropTypes.string,
        icon: PropTypes.oneOfType([PropTypes.node, PropTypes.array])
    }),
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.array])
};

//sst: custom selected element render
const SingleValue = props => {
    const iconType = props.data?.icon || null;
    return (
        <components.SingleValue {...props}>
            <span>
                {iconType && <Icon type={iconType} className="me-1" />}
                {props.children}
            </span>
        </components.SingleValue>
    );
};

SingleValue.propTypes = {
    data: PropTypes.shape({
        icon: PropTypes.oneOfType([PropTypes.node, PropTypes.array])
    }),
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.array])
};

const isValidNewOption = (inputValue, selectValue, selectOptions) => {
    let isValid = false;
    const trimmedInputValue = inputValue?.trim?.();
    if (trimmedInputValue) {
        let valueExists;
        const manualValues = selectOptions.find(
            e => 'manual' === e.id
        )?.options;
        if (manualValues) {
            const str = `EDITED::${trimmedInputValue}`;
            valueExists = manualValues.find(e => str === e.value);
        } else {
            const str = trimmedInputValue.toLowerCase();
            valueExists = selectOptions.find(e => {
                const val = String(e.label);
                return str === val || str === val.toLowerCase();
            });
        }
        isValid = !valueExists;
    }
    return isValid;
};

const Select = ({ className, innerClassNames, onChange, ...rest }) => {
    const requiredClassName = 'react-select-container form-control';

    const customCssClasses = [].concat(className);
    if (!customCssClasses.includes(requiredClassName)) {
        customCssClasses.push(requiredClassName);
    }

    const baseProps = {
        ...rest,
        classNames: {
            container: () => customCssClasses.join('\u0020'),
            ...innerClassNames
        },
        classNamePrefix: 'react-select',
        portalPlacement: 'bottom',
        menuPosition: 'fixed',
        menuPortalTarget: document.body,
        captureMenuScroll: true,
        styles: {
            // react-select.com/styles#the-styles-prop
            control: provided => {
                return {
                    ...provided,
                    backgroundColor: null,
                    borderColor: null,
                    borderRadius: null
                };
            },
            // container: NOOP,
            // clearIndicator: NOOP,
            dropdownIndicator: (base, state) => ({
                ...base,
                transition: 'all .2s ease',
                transform: state?.selectProps?.menuIsOpen
                    ? 'rotate(180deg)'
                    : null
            }),
            // group: NOOP,
            // groupHeading: NOOP,
            // indicatorsContainer: NOOP,
            indicatorSeparator: provided => {
                return {
                    ...provided,
                    backgroundColor: null
                };
            },
            // input: NOOP,
            // loadingIndicator: NOOP,
            // loadingMessage: NOOP,
            menu: provided => {
                return {
                    ...provided,
                    backgroundColor: null
                };
            },
            // menuList: NOOP,
            menuPortal: provided => {
                return {
                    ...provided,
                    zIndex: 9999
                };
            },
            // multiValue: NOOP,
            // multiValueLabel: NOOP,
            // multiValueRemove: NOOP,
            // noOptionsMessage: NOOP,
            option: provided => {
                return {
                    ...provided,
                    backgroundColor: null
                };
            }
            // placeholder: NOOP,
            // singleValue: NOOP,
            // valueContainer: NOOP
        },
        components: { Option: Option, SingleValue: SingleValue },
        onChange: value => {
            onChange?.(value || []);
        }
    };

    return rest.enableNewOptions ? (
        <CreatableSelect
            {...{ ...baseProps, isValidNewOption: isValidNewOption }}
            placeholder={
                rest.placeholder || (
                    <Translate message="Select or type a value" />
                )
            }
        />
    ) : rest.loadOptions ? (
        <AsyncSelect {...baseProps} />
    ) : (
        <ReactSelect {...baseProps} />
    );
};

Select.propTypes = {
    className: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    innerClassNames: PropTypes.object,
    enableNewOptions: PropTypes.bool,
    onChange: PropTypes.func
};

Select.defaultProps = {
    className: [],
    enableNewOptions: false
};

export default Select;
